import type { BusinessAddress, Community, Business } from '@/types';

export type ProfileType = 'user' | 'business' | 'user-business';

export interface PublicUser {
  id: string;
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  phone: string;
  image?: string; /*  */
  dob?: string | null;
  anniversaryDate?: string | null;
  dobVisibility?: 'PUBLIC' | 'FAMILY_ONLY' | 'FRIENDS_AND_FAMILY' | 'NONE' | null;
  anniversaryVisibility?: 'PUBLIC' | 'FAMILY_ONLY' | 'FRIENDS_AND_FAMILY' | 'NONE' | null;
  fieldVisibility?: Partial<
    Record<
      | 'email'
      | 'phone'
      | 'gender'
      | 'bloodGroup'
      | 'address'
      | 'city'
      | 'pincode'
      | 'dob'
      | 'anniversary',
      'PUBLIC' | 'FAMILY_ONLY' | 'FRIENDS_AND_FAMILY' | 'NONE'
    >
  > | null;
  emailVisibility?: 'PUBLIC' | 'FAMILY_ONLY' | 'FRIENDS_AND_FAMILY' | 'NONE' | null;
  phoneVisibility?: 'PUBLIC' | 'FAMILY_ONLY' | 'FRIENDS_AND_FAMILY' | 'NONE' | null;
  genderVisibility?: 'PUBLIC' | 'FAMILY_ONLY' | 'FRIENDS_AND_FAMILY' | 'NONE' | null;
  bloodGroupVisibility?: 'PUBLIC' | 'FAMILY_ONLY' | 'FRIENDS_AND_FAMILY' | 'NONE' | null;
  addressVisibility?: 'PUBLIC' | 'FAMILY_ONLY' | 'FRIENDS_AND_FAMILY' | 'NONE' | null;
  gender?: string | null;
  bloodGroup?: string | null;
  address?: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    country?: string;
    pincode?: string;
    googleMapLink?: string;
  } | null;
  socialLinks?: Record<string, string> | null;
  createdAt: string;
  updatedAt: string;
}

export interface PublicUserWithCommunities extends PublicUser {
  communities: (Community & {
    roles: string[];
    status: string;
    joinDate: string;
    isOnboarded: boolean;
  })[];
  businesses: Business[];
  familyRelations?: {
    id: string;
    relationship: string;
    relativeId: string;
    userId: string;
    relative: {
      firstName: string;
      lastName: string;
      image?: string | null;
    } | null;
  }[];
  friends?: {
    id: string;
    relativeId: string;
    userId: string;
    relative: {
      firstName: string;
      lastName: string;
      image?: string | null;
    } | null;
  }[];
  mutualFriends?: {
    id: string;
    relativeId: string;
    userId: string;
    relative: {
      firstName: string;
      lastName: string;
      image?: string | null;
    } | null;
  }[];
}

export interface PublicBusiness {
  id: string;
  name: string;
  category?: string;
  subCategory?: string;
  selfDefinedCategory?: string | null;
  address: string;
  addressJson?: BusinessAddress | Record<string, string>;
  phone: string;
  email: string;
  logo?: string;
  description?: string;
  speciality?: string;
  website?: string;
  websiteInfo?: string;
  keywords?: string;
  saleOffers?: string[];
  workArea?: 'Global' | 'National Level' | 'State Level' | 'Local';
  socialLinks?: Record<string, string> | null;
  role?: 'CREATOR' | 'JOINED';
  users?: {
    id: string;
    name: string;
    image?: string;
    role: string;
  }[];
  createdAt: string;
  updatedAt: string;
}

export interface UserBusinessProfile {
  id: string;
  userId: string;
  businessId: string;
  role: 'CREATOR' | 'JOINED';
  founderStory?: string;
  user: PublicUser;
  business: PublicBusiness;
  communities: (Community & {
    roles: string[];
    status: string;
  })[];
  createdAt: string;
  updatedAt: string;
}

export interface ProfileData {
  type: ProfileType;
  user?: PublicUserWithCommunities;
  business?: PublicBusiness;
  userBusiness?: UserBusinessProfile;
}

export interface ProfileHeaderProps {
  type: ProfileType;
  user?: PublicUser;
  business?: PublicBusiness;
  userBusiness?: UserBusinessProfile;
}

export interface ProfileLinkProps {
  userId?: string;
  businessId?: string;
  showAvatar?: boolean;
  showName?: boolean;
  avatarSize?: 'sm' | 'md' | 'lg';
  className?: string;
  children?: React.ReactNode;
}

export function getProfileUrl(props: ProfileLinkProps): string {
  if (props.userId && props.businessId) {
    return `/ub/${props.userId}/${props.businessId}`;
  }
  if (props.businessId) {
    return `/b/${props.businessId}`;
  }
  if (props.userId) {
    return `/u/${props.userId}`;
  }
  return '#';
}

export function getDisplayName(user?: PublicUser, business?: PublicBusiness): string {
  if (user && business) {
    return `${user.firstName} ${user.lastName} • ${business.name}`;
  }
  if (user) {
    return `${user.firstName} ${user.lastName}`.trim();
  }
  if (business) {
    return business.name;
  }
  return 'Unknown';
}

export function getDisplayInitials(user?: PublicUser, business?: PublicBusiness): string {
  if (user) {
    const first = user.firstName?.[0] || '';
    const last = user.lastName?.[0] || '';
    return (first + last).toUpperCase() || '?';
  }
  if (business) {
    return business.name[0]?.toUpperCase() || '?';
  }
  return '?';
}
